import type { AIMessage, AITicket } from '@/features/super-ai/types';

export type ConsultantHistoryTab = 'all' | 'ai' | 'wait' | 'owner' | 'incoming';

export type ConsultantSessionSnapshot = {
  conversationId: string | null;
  messages: AIMessage[];
  tickets: AITicket[];
  draft: string;
  tab: ConsultantHistoryTab;
  hasBooted: boolean;
};

const emptySession = (): ConsultantSessionSnapshot => ({
  conversationId: null,
  messages: [],
  tickets: [],
  draft: '',
  tab: 'all',
  hasBooted: false,
});

/** Module singleton — survives ConsultantPage unmount on Back/Forward. */
let snapshot: ConsultantSessionSnapshot = emptySession();
let bootPromise: Promise<void> | null = null;

export const readConsultantSession = (): ConsultantSessionSnapshot => snapshot;

export const writeConsultantSession = (next: ConsultantSessionSnapshot): void => {
  snapshot = next;
};

export const patchConsultantSession = (
  patch: Partial<ConsultantSessionSnapshot>,
): ConsultantSessionSnapshot => {
  snapshot = { ...snapshot, ...patch };
  return snapshot;
};

export const clearConsultantSession = (): void => {
  snapshot = emptySession();
  bootPromise = null;
};

/**
 * Deduped boot for React Strict Mode remounts.
 * `run` must write the finished snapshot via writeConsultantSession.
 */
export const ensureConsultantBoot = (run: () => Promise<void>): Promise<void> => {
  if (snapshot.hasBooted) return Promise.resolve();
  if (!bootPromise) {
    bootPromise = run()
      .catch(() => undefined)
      .finally(() => {
        bootPromise = null;
      });
  }
  return bootPromise;
};
